import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import setPageMetadata from "@insite/client-framework/Common/Utilities/setPageMetadata";
import { trackPageChange } from "@insite/client-framework/Common/Utilities/tracking";
import { ParentProductIdContext } from "@insite/client-framework/Components/ParentProductContext";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import Zone from "@insite/client-framework/Components/Zone";
import { getUrl } from "@insite/client-framework/ServerSideRendering";
import {
    getSelectedProductPath,
    getSession,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getComputedVariantProduct,
    getProductState,
    getProductStateByPath,
} from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import changeQtyOrdered from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/ChangeQtyOrdered";
import changeUnitOfMeasure from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/ChangeUnitOfMeasure";
import displayProduct from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/DisplayProduct";
import generateStructuredPageData from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/GenerateStructuredPageData";
import resetSelections from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/ResetSelections";
import setMetadataSetForId from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/SetMetadataSetForId";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import { HideFooter, HideHeader, StructuredPageData } from "@insite/client-framework/Types/FieldDefinition";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import CurrentCategory from "@insite/content-library/Components/CurrentCategory";
import Modals from "@insite/content-library/Components/Modals";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Page from "@insite/mobius/Page";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { LoadingOverlay } from "@sonic-equipment/ui";
import { ProductDetailsPageDrawer } from "@sonic/CustomComponents/ProductDetailsPageDrawer";
import StickyFooter from "@sonic/CustomComponents/StickyFooter";
import resetProductDetailNavigationState from "@sonic/Store/Components/ProductDetailNavigation/Handlers/ResetProductDetailNavigationState";
import { customerState } from "@sonic/Store/GemecoState";
import AnchorNavigation from "@sonic/Widgets/Common/AnchorNavigation";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: customerState) => {
    const location = getLocation(state);
    let productPath =
        getSelectedProductPath(state) ||
        (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
    let queryString = location.search;
    if (location.pathname.toLowerCase().startsWith("/content/page/") && productPath.indexOf("?") > -1) {
        queryString = productPath ? productPath.substring(productPath.indexOf("?")) : "";
        productPath = productPath.substring(0, productPath.indexOf("?"));
    }

    const productState = getProductStateByPath(state, productPath);
    const catalogPageState = getCatalogPageStateByPath(state, productPath);
    const variantProductState = getProductState(state, state.pages.productDetails.selectedProductId);
    const computedVariantProduct = getComputedVariantProduct(productState, variantProductState);
    const settingsCollection = getSettingsCollection(state);
    return {
        productState: computedVariantProduct,
        variantProductState,
        catalogPageState,
        parentProductState: productState,
        productInfo:
            computedVariantProduct.value && state.pages.productDetails.productInfosById
                ? state.pages.productDetails.productInfosById[computedVariantProduct.value.id]
                : undefined,
        productPath,
        queryString,
        lastProductPath: state.pages.productDetails.lastProductPath,
        lastStyledOption: state.pages.productDetails.lastStyledOption,
        websiteName: state.context.website.name,
        page: getCurrentPage(state),
        location,
        websiteSettings: settingsCollection.websiteSettings,
        searchPath: settingsCollection.searchSettings.searchPath,
        selectedProductId: state.pages.productDetails.selectedProductId,
        isProductLoading: state.pages.productDetails.isProductLoading,
        metadataSetForId: state.pages.productDetails.metadataSetForId,
        structuredPageData: state.pages.productDetails.structuredPageData,
        structuredPageDataSetForId: state.pages.productDetails.structuredPageDataSetForId,
        session: getSession(state),
        anchorNavigation: state.productDetailNavigation, // SONIC
    };
};

const mapDispatchToProps = {
    displayProduct,
    changeQtyOrdered,
    changeUnitOfMeasure,
    resetSelections,
    generateStructuredPageData,
    setMetadataSetForId,
    resetProductDetailNavigationState,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & PageProps;

interface State {
    metadataSetForId?: string;
    transform: number;
    showheader?: boolean;
    containerHeight: number;
}

class ProductDetailsPage extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            transform: 0,
            showheader: false,
            containerHeight: 250,
        };
    }

    UNSAFE_componentWillMount() {
        this.displayProductIfNeeded();
        this.trySetMetadata();
    }

    componentDidUpdate(): void {
        this.displayProductIfNeeded();

        const { productState } = this.props;
        if (productState.value && productState.value.id !== this.props.metadataSetForId && this.trySetMetadata()) {
            trackPageChange();
        }
    }

    componentWillUnmount() {
        this.props.resetSelections();
        this.props.resetProductDetailNavigationState({ navigationLinks: [] });
    }

    trySetMetadata() {
        const {
            productState: { value: product },
            catalogPageState,
            websiteName,
            location,
            websiteSettings,
            page,
            structuredPageData,
            productInfo,
            session,
            metadataSetForId,
            structuredPageDataSetForId,
            anchorNavigation,
        } = this.props;

        if (!catalogPageState?.value || !product) {
            return false;
        }

        if (page.fields.enableStructuredPageData && product.id !== structuredPageDataSetForId) {
            this.props.generateStructuredPageData({
                product,
                productInfo,
                session,
                brandId: catalogPageState.value.brandId || product.brand?.id,
                categoryIdWithBrandId: catalogPageState.value.categoryIdWithBrandId,
                categoryId: catalogPageState.value.categoryId,
            });
            return false;
        }

        const {
            metaDescription,
            metaKeywords,
            openGraphImage,
            openGraphTitle,
            openGraphUrl,
            title,
            alternateLanguageUrls,
        } = catalogPageState.value;

        const pageMetaData = {
            metaDescription,
            metaKeywords,
            openGraphImage,
            openGraphTitle,
            openGraphUrl,
            currentPath: location.pathname,
            canonicalPath: product.canonicalUrl,
            title: title || product.productTitle,
            websiteName,
            alternateLanguageUrls: alternateLanguageUrls ?? undefined,
            structuredPageData,
        };

        if (catalogPageState.value.productId !== this.props.selectedProductId) {
            const variantContent = this.props.variantProductState.value?.content;
            if (variantContent) {
                const url: { protocol: string; host: string } = IS_SERVER_SIDE ? getUrl()! : window.location;
                const urlAddress = `${url.protocol.toLowerCase()}//${url.host.toLowerCase()}`;
                const imagePath =
                    variantContent.openGraphImage ||
                    product.smallImagePath ||
                    product.mediumImagePath ||
                    product.largeImagePath;

                const openGraphImage =
                    !imagePath || imagePath.indexOf("http://") > -1 || imagePath.indexOf("https://") > -1
                        ? imagePath
                        : urlAddress + imagePath.startsWith("/")
                        ? imagePath
                        : `/${imagePath}`;
                let openGraphUrl = variantContent.openGraphUrl || product.canonicalUrl;

                openGraphUrl =
                    !openGraphUrl || openGraphUrl.indexOf("http://") > -1 || openGraphUrl.indexOf("https://") > -1
                        ? openGraphUrl
                        : urlAddress + openGraphUrl.startsWith("/")
                        ? openGraphUrl
                        : `/${openGraphUrl}`;
                const pageTitle = variantContent.pageTitle || product.productTitle;

                pageMetaData.title = pageTitle;
                pageMetaData.metaKeywords =
                    variantContent.metaKeywords || `${product.productTitle} ${product.productNumber}`;
                pageMetaData.metaDescription = variantContent.metaDescription || product.productTitle;
                pageMetaData.openGraphTitle = variantContent.openGraphTitle || pageTitle;
                pageMetaData.openGraphImage = openGraphImage || "";
                pageMetaData.openGraphUrl = openGraphUrl || "";
            }
        }

        if (!openGraphImage && !pageMetaData.openGraphImage && product.images && product.images?.length > 0) {
            pageMetaData.openGraphImage = product.images[0].smallImagePath || product.images[0].mediumImagePath;
        }

        setPageMetadata(pageMetaData, websiteSettings);

        if (product.id !== metadataSetForId) {
            this.props.setMetadataSetForId({
                metadataSetForId: product.id,
            });
        }
        return true;
    }

    displayProductIfNeeded() {
        const { productPath, queryString, lastProductPath, lastStyledOption, isProductLoading, searchPath } =
            this.props;
        const queryParams = parseQueryString<{ option?: string; criteria?: string }>(queryString.replace("?", ""));
        const styledOption = (
            queryParams.option?.toString() ||
            queryParams.criteria?.toString() ||
            ""
        ).toLocaleLowerCase();
        if (
            (productPath.toLowerCase() === lastProductPath?.toLowerCase() &&
                styledOption === lastStyledOption?.toLowerCase()) ||
            productPath.toLowerCase() === `/${searchPath.toLowerCase()}`
        ) {
            return;
        }

        if (isProductLoading) {
            return;
        }

        this.props.displayProduct({ path: productPath, styledOption });
    }

    render() {
        if (
            !this.props.productState.value ||
            this.props.productState.isLoading ||
            !this.props.productInfo ||
            (this.props.productPath &&
                this.props.lastProductPath &&
                this.props.productPath.toLowerCase() !== this.props.lastProductPath.toLowerCase())
        ) {
            return <LoadingOverlay />;
        }

        const productId = this.props.productState.value.id;

        const productContext: ProductContextModel = {
            product: this.props.productState.value as ProductModel,
            productInfo: this.props.productInfo,
            onQtyOrderedChanged: o => {
                this.props.changeQtyOrdered({
                    qtyOrdered: o,
                    productId,
                });
            },
            onUnitOfMeasureChanged: o => {
                this.props.changeUnitOfMeasure({
                    unitOfMeasure: o,
                    productId,
                });
            },
        };
        const wrapperStyle = {
            css: css`
                position: sticky;
                z-index: 2;
                background-color: white;
                top: 0;
                box-shadow: 0 7px 15px rgba(0, 0, 0, 0.1);
                transition: padding-top 0.2s ease;

                @keyframes slidein {
                    from {
                        transform: translateY(-107px);
                    }
                    to {
                        transform: translateY(0);
                    }
                }

                animation: slidein 0.2s linear forwards;
                ${({ theme }: { theme: BaseTheme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            top: 0;
                        `,
                        css`
                            top: 0;
                        `,
                        css`
                            top: 65px;
                        `,
                        css`
                            top: 0;
                        `,
                        null,
                    ]);
                }}
            `,
        };

        const pagePaddingStyle = css`
            overflow: unset !important;
            ${({ theme }: { theme: BaseTheme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        padding-bottom: 96px;
                    `,
                    css`
                        padding-bottom: 96px;
                    `,
                    css`
                        padding-bottom: 96px;
                    `,
                ]);
            }}
        `;
        const stickyNav = {
            css: css`
                padding: 0 15px;
                position: sticky;
                z-index: 2;
                background-color: white;
                top: 150px;
                border-top: 1px solid #e8e8e8;
                box-shadow: 0 7px 15px rgba(0, 0, 0, 0.1);
                width: 100%;
                display: flex;
                justify-content: center;
                ${({ theme }: { theme: BaseTheme }) => {
                    return breakpointMediaQueries(theme, [
                        css`
                            top: 53px;
                        `,
                        css`
                            top: 53px;
                        `,
                        css`
                            top: 54px;
                        `,
                        css`
                            top: 72px;
                        `,
                        css`
                            top: 96px;
                        `,
                    ]);
                }}
            `,
        };

        const wrapperStyleTop = {
            css: css`
                @keyframes slide-top {
                    0% {
                        max-height: 600px;
                        overflow: unset;
                    }
                    100% {
                        max-height: 200px;
                        overflow: hidden;
                    }
                }
                animation: slide-top 0.2s linear reverse;
            `,
        };

        return (
            <Page
                css={pagePaddingStyle}
                data-test-selector={`productDetails_productId_${productId}`}
                fullWidth={[true, true, true, true, true]}
            >
                <CurrentCategory>
                    <ProductContext.Provider value={productContext}>
                        <ParentProductIdContext.Provider value={this.props.parentProductState?.value?.id}>
                            <StyledWrapper {...wrapperStyleTop}>
                                <Zone contentId={this.props.id} zoneName="TopInformation" />
                            </StyledWrapper>

                            {/*
                            <StyledWrapper {...wrapperStyle}>
                                <Hidden below="md">
                                    <Zone contentId={this.props.id} zoneName="stickyTopInformation" />
                                </Hidden>
                            </StyledWrapper>
                            */}

                            <StyledWrapper {...stickyNav}>
                                <AnchorNavigation navigationItems={this.props.anchorNavigation.navigationLinks} />
                            </StyledWrapper>
                            <Zone contentId={this.props.id} zoneName="Content" />
                            <Modals />
                            <StickyFooter marginLeft={false}>
                                <ProductDetailsPageDrawer product={productContext}>
                                    <Zone contentId={this.props.id} zoneName="ProductDetailsPageDrawer" />
                                </ProductDetailsPageDrawer>
                            </StickyFooter>
                        </ParentProductIdContext.Provider>
                    </ProductContext.Provider>
                </CurrentCategory>
            </Page>
        );
    }
}

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductDetailsPage),
    definition: {
        hasEditableUrlSegment: false,
        hasEditableTitle: false,
        supportsProductSelection: true,
        pageType: "System",
        fieldDefinitions: [StructuredPageData, HideHeader, HideFooter],
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "ProductDetailsPage" instead of this constant.
 */
export const ProductDetailsPageContext = "ProductDetailsPage";
